import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import Footer from '@/components/layout/Footer';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [cadastroDialogOpen, setCadastroDialogOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleCadastrarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setCadastroDialogOpen(true);
  };

  const handleEntrarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setLoginDialogOpen(true);
  };

  const handleLogin = () => {
    setIsLoading(true);
    
    // Simulação de login
    setTimeout(() => {
      if (!email || !password) {
        alert('Por favor, preencha todos os campos.');
        setIsLoading(false);
        return;
      }

      // Normalizar email e password (trim e lowercase)
      const normalizedEmail = email.trim().toLowerCase();
      const normalizedPassword = password.trim();

      console.log("Tentativa de login:", { normalizedEmail, normalizedPassword });

      // Verificar se são credenciais de parceiro primeiro
      const partnerCredentials = [
        {
          email: "avioes@empresa.com",
          password: "senha123",
          companyId: "1",
          companyName: "Aviões S.A.",
          companyType: "aviation",
          cnpj: "12.345.678/0001-90"
        },
        {
          email: "hotel@empresa.com",
          password: "senha123",
          companyId: "2",
          companyName: "Hotel Mar do Brasil Ltda",
          companyType: "hotel",
          cnpj: "98.765.432/0001-10"
        }
      ];

      const partner = partnerCredentials.find(
        (p) => {
          const emailMatch = p.email.toLowerCase() === normalizedEmail;
          const passwordMatch = p.password === normalizedPassword;
          console.log(`Verificando ${p.email}: email=${emailMatch}, password=${passwordMatch}`);
          return emailMatch && passwordMatch;
        }
      );

      if (partner) {
        // É um parceiro - redirecionar para portal do parceiro
        console.log("✅ Parceiro encontrado:", partner);
        localStorage.setItem("partnerToken", "mock-token-" + partner.companyId);
        localStorage.setItem("partnerCompanyId", partner.companyId);
        localStorage.setItem("partnerCompanyName", partner.companyName);
        localStorage.setItem("partnerCompanyType", partner.companyType);
        localStorage.setItem("partnerCNPJ", partner.cnpj);
        
        // Limpar dados de login normal para evitar conflitos
        localStorage.removeItem('isLoggedIn');
        
        alert(`Login realizado com sucesso! Bem-vindo, ${partner.companyName}`);
        
        // Forçar redirecionamento completo - usar replace para não permitir voltar
        setTimeout(() => {
          window.location.replace("/partner/dashboard");
        }, 100);
        return;
      }
      
      console.log("❌ Parceiro não encontrado");
      
      console.log("Não é parceiro, verificando outras credenciais...");
      
      // Verificar credenciais de cliente (João)
      const clientEmail = 'joao.silva@email.com';
      const clientPassword = 'joao123';
      
      if (normalizedEmail === clientEmail && normalizedPassword === clientPassword) {
        // Gera token aleatório de 6 dígitos
        const token = Math.floor(100000 + Math.random() * 900000).toString();
        setIsLoading(false);
        setLoginDialogOpen(false);
        setEmail('');
        setPassword('');
        // Redireciona para verificação com token
        navigate('/client/verification', { state: { email: normalizedEmail, token } });
        return;
      }
      
      // Verificar credenciais normais (admin)
      if (normalizedEmail === 'admin@empresa.com' || normalizedEmail === 'admin@banclima.com' || normalizedEmail === 'admin@banclima.org.br') {
        localStorage.setItem('isLoggedIn', 'true');
        alert('Login realizado com sucesso! Redirecionando para o painel administrativo.');
        window.location.href = '/admin';
        setIsLoading(false);
        return;
      }
      
      // Se não for parceiro, admin nem cliente, mostrar erro
      alert('Email ou senha incorretos.\n\nPara parceiros: avioes@empresa.com ou hotel@empresa.com (senha: senha123)\nPara cliente: joao.silva@email.com (senha: joao123)');
      setIsLoading(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Cabeçalho */}
      <header className="bg-[#0e7cb4] text-white py-4">
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex flex-col">
            <div className="mb-0">
              <img 
                src="/lovable-uploads/LOGO-BANCLIMA--BRANCA.png" 
                alt="BANCLIMA" 
                className="h-14"
                onError={(e) => {
                  // Tentar com extensões diferentes se a primeira falhar
                  const target = e.currentTarget;
                  const currentSrc = target.src;
                  
                  if (currentSrc.endsWith('.png')) {
                    target.src = currentSrc.replace('.png', '.jpg');
                  } else if (currentSrc.endsWith('.jpg')) {
                    target.src = currentSrc.replace('.jpg', '.jpeg');
                  } else if (currentSrc.endsWith('.jpeg')) {
                    target.src = currentSrc.replace('.jpeg', '.svg');
                  } else if (currentSrc.endsWith('.svg')) {
                    target.src = '/lovable-uploads/LOGO-BANCLIMA--BRANCA';
                  } else {
                    // Se todas as tentativas falharem, mostrar o texto
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      const textLogo = document.createElement('div');
                      textLogo.className = 'font-bold text-[3.5rem] tracking-widest';
                      textLogo.textContent = 'BANCLIMA';
                      parent.appendChild(textLogo);
                    }
                  }
                }}
              />
            </div>
            <div className="text-sm font-light tracking-wider pl-2">MITIGAÇÃO E COMPENSAÇÃO DE GEE</div>
          </div>
          <div className="text-4xl font-bold leading-tight tracking-wide text-right pr-24 pl-16">
            MITIGAÇÃO E COMPENSAÇÃO DE CARBONO
          </div>
        </div>
      </header>

      {/* Conteúdo principal */}
      <main className="flex-grow flex flex-col">
        <div className="flex flex-col md:flex-row flex-grow">
          {/* Imagem de satélite */}
          <div className="w-full md:w-2/3 h-full">
            <img 
              src="/lovable-uploads/Imagem-mapa.png" 
              alt="Mapa de dados climáticos" 
              className="w-full h-full object-cover"
              style={{ minHeight: '100%' }}
              onError={(e) => {
                // Tentar com extensões diferentes se a primeira falhar
                const target = e.currentTarget;
                const currentSrc = target.src;
                
                if (currentSrc.endsWith('.png')) {
                  target.src = currentSrc.replace('.png', '.jpg');
                } else if (currentSrc.endsWith('.jpg')) {
                  target.src = currentSrc.replace('.jpg', '.jpeg');
                } else if (currentSrc.endsWith('.jpeg')) {
                  target.src = currentSrc.replace('.jpeg', '.svg');
                } else if (currentSrc.endsWith('.svg')) {
                  target.src = '/lovable-uploads/Imagem-mapa';
                }
              }}
            />
          </div>

          {/* Texto explicativo */}
          <div className="w-full md:w-1/3 p-8 flex flex-col">
            <h2 className="text-xl font-bold mb-4 text-center">MITIGAÇÃO E COMPENSAÇÃO DE GEE</h2>
            
            <p className="mb-4">
              A mitigação de GEE envolve ações para reduzir ou evitar a emissão de gases de efeito estufa na atmosfera. Isso inclui medidas como o uso de fontes de energia renovável, aumento da eficiência energética, reflorestamento e mudanças nos padrões de produção e consumo.
            </p>
            
            <p className="mb-4">
              Já a compensação refere-se a neutralizar as emissões inevitáveis por meio de projetos que removem ou evitam emissões em outras áreas, como programas de créditos de carbono, preservação de florestas e captura de carbono.
            </p>
            
            <p className="mb-4">
              Ambas as estratégias são fundamentais para combater as mudanças climáticas e cumprir metas de sustentabilidade.
            </p>
            
            <div className="flex flex-col items-center mt-auto mb-8 space-y-3">
              <Button 
                className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white px-6 py-2"
                onClick={handleEntrarClick}
              >
                ENTRAR NA PLATAFORMA
              </Button>
              <Button 
                className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white px-6 py-2"
                onClick={handleCadastrarClick}
              >
                CADASTRAR
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Modal de Login */}
      <Dialog open={loginDialogOpen} onOpenChange={setLoginDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Login na Plataforma</DialogTitle>
            <DialogDescription className="text-center">
              Digite suas credenciais para acessar a plataforma Banclima.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4] focus:border-transparent"
                placeholder="Digite seu e-mail"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4] focus:border-transparent"
                placeholder="Digite sua senha"
              />
            </div>
          </div>
          <DialogFooter className="sm:justify-center space-x-2">
            <button 
              className="px-4 py-2 rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 font-medium"
              onClick={() => {
                setLoginDialogOpen(false);
                setEmail('');
                setPassword('');
              }}
            >
              Cancelar
            </button>
            <button 
              className="px-4 py-2 rounded bg-[#0e7cb4] hover:bg-[#0a5c87] text-white font-medium disabled:opacity-50"
              onClick={handleLogin}
              disabled={isLoading}
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Cadastro */}
      <Dialog open={cadastroDialogOpen} onOpenChange={setCadastroDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Cadastro na Plataforma</DialogTitle>
            <DialogDescription className="text-center">
              Preencha seus dados para criar sua conta na plataforma Banclima.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4] focus:border-transparent"
                placeholder="Digite seu nome completo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4] focus:border-transparent"
                placeholder="Digite seu e-mail"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
              <input
                type="tel"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4] focus:border-transparent"
                placeholder="Digite seu telefone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CPF/CNPJ</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4] focus:border-transparent"
                placeholder="Digite seu CPF ou CNPJ"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Empresa (opcional)</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4] focus:border-transparent"
                placeholder="Digite o nome da empresa"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <input
                type="password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4] focus:border-transparent"
                placeholder="Digite sua senha"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar Senha</label>
              <input
                type="password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4] focus:border-transparent"
                placeholder="Confirme sua senha"
              />
            </div>
          </div>
          <DialogFooter className="sm:justify-center space-x-2">
            <button 
              className="px-4 py-2 rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 font-medium"
              onClick={() => setCadastroDialogOpen(false)}
            >
              Cancelar
            </button>
            <button 
              className="px-4 py-2 rounded bg-[#0e7cb4] hover:bg-[#0a5c87] text-white font-medium"
              onClick={() => {
                alert("Cadastro enviado com sucesso! Verifique seu e-mail para ativar sua conta.");
                setCadastroDialogOpen(false);
              }}
            >
              Criar Conta
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rodapé */}
      <Footer />
    </div>
  );
};

export default LandingPage; 